import React, { useEffect, useMemo } from 'react';
import Module from '../../../layout/Module';
import { useDispatch, useSelector } from 'react-redux';
import { FormControl, MenuItem, Select, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { setCurrentCurriculum } from '../../../../store/actions/assessment.actions';
import Loading from '../../../Loading/Loading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateSkip } from '../../../../store/actions/datatable.actions';
import { toggleAlertModule } from '../../../../store/actions/alert.actions';
import { updateFilter } from '../../../../store/actions/filter.actions';

const useStyles = makeStyles(() => ({
  formControl: {
    width: 150,
    backgroundColor: 'var(--background)',
    borderRadius: '0.25rem',
    padding: '0'
  },
  select: {
    padding: '0.5rem 0.75rem '
  }
}));

const MessageAlert = () => (
  <div className='flex flex-col justify-center items-center h-full'>
    <FontAwesomeIcon
      icon={['fas', 'exclamation-triangle']}
      size='3x'
      className='text-muted-foregroud opacity-25'
    />
    <Typography className='text-muted-foregroud opacity-25'>No curriculum available</Typography>
  </div>
);

const AssessmentModule = ({ children, title, titleLink, actions, moduleName }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();
  const classes = useStyles();
  const curriculumId = params['*']?.split('/')[0];
  const curriculumList = useSelector((state) => state?.assessment?.curriculum || []);
  const isLoading = useSelector((state) => state?.assessment?.loading);
  const alertSave = useSelector((state) => state?.alert?.save || {});
  const current = useSelector((state) => state?.assessment?.currentCurriculum);
  const permissionsListClass = useSelector((state) => state?.session?.permissions?.class?.list || []);
  const existLinceseAssessment = useSelector((state) =>
    state?.tenant?.license?.find((license) => license.code === 'assessment')
  );
  const { pathname } = useLocation();

  const onCurriculumChange = (value) => {
    const onClick = () => {
      dispatch(setCurrentCurriculum(value));
      navigate(`${titleLink}/${value}`);
      dispatch(updateSkip(moduleName, 0));
      dispatch(updateFilter(moduleName, {}));
    };

    if (alertSave && alertSave.mode) {
      dispatch(
        toggleAlertModule(true, alertSave.submodule, alertSave.mode, alertSave.onSave, () => {
          onClick();
        })
      );
    } else {
      onClick();
    }
  };

  useEffect(() => {
    if (!curriculumId) {
      onCurriculumChange('all');
    }
  }, [pathname]);

  useEffect(() => {
    if (curriculumId && curriculumId !== current?._id) {
      dispatch(setCurrentCurriculum(curriculumId));
    }
  }, [current, curriculumId]);

  const curriculum = useMemo(() => {
    let list = [
      {
        _id: 'all',
        shortName: 'All',
        name: 'ALL'
      }
    ];
    if (existLinceseAssessment && permissionsListClass.includes('all')) {
      const common = [
        {
          _id: 'all',
          shortName: 'All',
          name: 'ALL'
        },
        {
          _id: 'assigned',
          shortName: 'Assigned',
          name: 'Assigned',
          divider: true
        },
        {
          _id: 'unassigned',
          shortName: 'Unassigned',
          name: 'Unassigned'
        }
      ];
      list = [...common, ...curriculumList];
    }
    return list;
  }, [curriculumList]);

  return (
    <Module moduleName={moduleName}>
      <Module.Header title={title} classNameTitle='text-nowrap min-w-230px max-w-230px'>
        <div className='flex justify-between items-center w-full gap-2'>
          <div className=''>
            {curriculum?.length > 1 && (
              <FormControl variant='outlined' className={classes.formControl}>
                <Select
                  variant='outlined'
                  value={curriculumId}
                  onChange={(e) => onCurriculumChange(e.target.value)}
                  classes={{
                    outlined: classes.select
                  }}
                  MenuProps={{
                    className: classes.select,
                    style: {
                      padding: '0.5rem'
                    }
                  }}
                >
                  {curriculum?.map((cur, i) => (
                    <MenuItem key={`curriculum-${cur._id}`} value={cur._id}>
                      {cur.shortName} Curriculum
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </div>
          <div className='flex justify-end items-center gap-2'>{actions}</div>
        </div>
      </Module.Header>
      <Module.Body>
        {isLoading ? <Loading /> : curriculum?.length === 0 ? <MessageAlert /> : children}
      </Module.Body>
    </Module>
  );
};

export default AssessmentModule;
